<template>
  <DropdownContent
    v-if="schemaRequired"
    id="configurationRequired"
    :title="`Required fields (${Object.values(schemaRequired).length})`"
  >
    <div
      class="field"
      v-for="(field, key) in schemaRequired"
      :key="key"
      :field="field"
    >
      <label class="field-label is-normal">{{ field.title }}</label>
      <div class="field-body">
        <component
          v-if="FIELDS[field.type]"
          :is="FIELDS[field.type].component"
          :field="field"
          :field-id="`config-field-id-${uid}-${key}`"
          :model-value="configuration[key] ?? defaultConfiguration[key]"
          v-on:update:model-value="(newValue: unknown) => (configuration[key] = newValue)"
          :class="{ 'is-danger': Boolean(errors[key]) }"
          :worker-id="workerId"
        />
      </div>
      <!-- For list fields, the errors are displayed below the corresponding list item -->
      <p
        class="help is-danger"
        v-if="errors[key] && field.type !== 'list'"
      >
        {{ errors[key] }}
      </p>
    </div>
  </DropdownContent>
  <DropdownContent
    id="configurationOptional"
    v-if="schemaOptional"
    :title="`Optional fields (${Object.values(schemaOptional).length})`"
    class="mt-2"
  >
    <div
      class="field"
      v-for="(field, key) in schemaOptional"
      :key="key"
      :field="field"
    >
      <label class="field-label is-normal">{{ field.title }}</label>
      <div class="field-body">
        <component
          v-if="FIELDS[field.type]"
          :is="FIELDS[field.type].component"
          :field="field"
          :field-id="`config-field-id-${uid}-${key}`"
          :class="{ 'is-danger': Boolean(errors[key]) }"
          :model-value="configuration[key] ?? defaultConfiguration[key]"
          v-on:update:model-value="(newValue: unknown) => (configuration[key] = newValue)"
          :worker-id="workerId"
        />
      </div>
      <!-- For list fields, the errors are displayed below the corresponding list item -->
      <p
        class="help is-danger"
        v-if="errors[key] && field.type !== 'list'"
      >
        {{ errors[key] }}
      </p>
    </div>
  </DropdownContent>
</template>

<script lang="ts">
import { cloneDeep, uniqueId } from 'lodash'
import { type PropType, defineComponent } from 'vue'

import DropdownContent from '@/components/DropdownContent.vue'
import FIELDS from '@/components/Process/Workers/Configurations/ConfigurationForm/Fields'
import type { UUID } from '@/types'
import type { UserConfigurationField } from '@/types/workerConfiguration'

export default defineComponent({
  components: { DropdownContent },
  emits: ['update:modelValue', 'configuration-error'],
  props: {
    schema: {
      type: Object as PropType<{ [k: string]: UserConfigurationField } | null>,
      required: true,
    },
    workerId: {
      type: String as PropType<UUID>,
      required: true,
    },
    modelValue: {
      type: Object as PropType<Record<string, unknown>>,
      required: true,
    },
  },
  data: () => ({
    FIELDS,
    uid: uniqueId(),
    configuration: {} as Record<string, unknown>,
    errors: {} as Record<string, string>,
  }),
  mounted() {
    this.configuration = cloneDeep(this.modelValue)
  },
  computed: {
    schemaRequired() {
      if (!this.schema) return null
      return Object.fromEntries(Object.entries(this.schema).filter(([, field]) => field.required))
    },
    schemaOptional() {
      if (!this.schema) return null
      return Object.fromEntries(Object.entries(this.schema).filter(([, field]) => !field.required))
    },
    defaultConfiguration() {
      if (!this.schema) return {}
      const filledForm: Record<string, unknown> = {}
      for (const property in this.schema) {
        filledForm[property] = this.schema[property].default ?? ''
      }
      return filledForm
    },
  },
  methods: {
    onConfigChange(newValue: Record<string, unknown>) {
      if (!newValue || !Object.keys(newValue).length) return

      // There is no schema to validate the configuration against, send it to the parent component as it is
      if (!this.schema) {
        this.$emit('update:modelValue', newValue)
        return
      }

      const errors: Record<string, string> = {}
      const validatedConfig: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(newValue)) {
        const field = this.schema[k]
        if (!field) {
          errors[k] = `Unrecognised field: ${k}`
          continue
        }
        if (!FIELDS[field.type]) {
          errors[k] = `Unknown field type: ${k}`
          continue
        }

        // Detect a missing required field
        if (field.required === true) {
          /*
           * !v alone does not work to check that the value of a required field is not empty,
           * because !0 is true and 0 can be a valid value; !String(v).length returns false if
           * 0 (or 00000 etc) is entered, however it does not work to check against null,
           * which is why there is a second (v !== 0 && !v) check.
           */
          if (!String(v).length) errors[k] = 'This field is required.'
          else if (v !== 0 && !v) errors[k] = 'This field is required.'
        }

        // Apply a validation function, if there is one for this field type and there is a value set
        if (v && FIELDS[field.type].validate !== undefined) {
          try {
            // @ts-expect-error Each validation function only accepts the UserConfigurationField of their own type, but TS does not understand the intersection of all fields
            validatedConfig[k] = FIELDS[field.type].validate(v, field)
          } catch (e) {
            if (e instanceof Error) errors[k] = e.message
          }
        }
      }

      this.errors = errors
      if (!Object.keys(errors).length) this.$emit('update:modelValue', validatedConfig)
    },
  },
  watch: {
    /**
     * On any update, validate the configuration fields values against the schema,
     * and only send valid configurations as updates to modelValue.
     * Unexpected fields (absent from the schema) are handled by the parent component in JSONConfigError.
     */
    configuration: {
      immediate: true,
      deep: true,
      handler: 'onConfigChange',
    },

    errors: {
      immediate: true,
      handler(newValue: Record<string, string>) {
        this.$emit('configuration-error', Object.keys(newValue).length > 0)
      },
    },
  },
})
</script>
